<script setup>
import { computed, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { emitter } from 'shared/helpers/mitt';
import InvoicesAPI from 'dashboard/api/invoices';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';

const props = defineProps({
  conversationId: {
    type: [Number, String],
    required: true,
  },
});

const emit = defineEmits(['close']);

const { t } = useI18n();

const dialogRef = ref(null);
const items = ref([{ name: '', quantity: 1, unitAmount: '' }]);
const isSubmitting = ref(false);

onMounted(() => {
  dialogRef.value?.open();
});

const itemCents = item => {
  const cents = Math.round(parseFloat(item.unitAmount) * 100);
  const quantity = parseInt(item.quantity, 10) || 0;
  return Number.isNaN(cents) ? 0 : cents * quantity;
};

const totalCents = computed(() =>
  items.value.reduce((sum, item) => sum + itemCents(item), 0)
);

const formattedTotal = computed(
  () => `NPR ${(totalCents.value / 100).toFixed(2)}`
);

const isValid = computed(
  () =>
    items.value.length > 0 &&
    items.value.every(
      item =>
        item.name.trim() &&
        (parseInt(item.quantity, 10) || 0) > 0 &&
        parseFloat(item.unitAmount) >= 0
    ) &&
    totalCents.value > 0
);

const addItem = () => {
  items.value.push({ name: '', quantity: 1, unitAmount: '' });
};

const removeItem = index => {
  items.value.splice(index, 1);
};

const onSubmit = async () => {
  if (!isValid.value || isSubmitting.value) return;

  isSubmitting.value = true;
  try {
    await InvoicesAPI.createForConversation(props.conversationId, {
      provider: 'khalti',
      items: items.value.map(item => ({
        name: item.name.trim(),
        quantity: parseInt(item.quantity, 10) || 1,
        unit_amount: item.unitAmount,
      })),
    });
    useAlert(t('INVOICES.CREATE.SUCCESS'));
    emitter.emit('payments.invoiceCreated');
    emit('close');
  } catch (error) {
    useAlert(error?.response?.data?.error || t('INVOICES.CREATE.ERROR'));
  } finally {
    isSubmitting.value = false;
  }
};
</script>

<template>
  <Dialog
    ref="dialogRef"
    width="xl"
    overflow-y-auto
    :title="$t('INVOICES.CREATE.TITLE')"
    :description="$t('INVOICES.CREATE.DESC')"
    :confirm-button-label="$t('INVOICES.CREATE.SUBMIT')"
    :cancel-button-label="$t('INVOICES.CREATE.CANCEL')"
    :disable-confirm-button="!isValid || isSubmitting"
    :is-loading="isSubmitting"
    @confirm="onSubmit"
    @close="emit('close')"
  >
    <div class="flex flex-col w-full gap-4">
      <div class="flex flex-col gap-2">
        <div
          class="grid grid-cols-[1fr_4rem_7rem_2rem] gap-2 text-xs font-medium text-n-slate-11"
        >
          <span>{{ $t('INVOICES.CREATE.ITEM_NAME') }}</span>
          <span>{{ $t('INVOICES.CREATE.QUANTITY') }}</span>
          <span>{{ $t('INVOICES.CREATE.UNIT_AMOUNT') }}</span>
          <span />
        </div>
        <div
          v-for="(item, index) in items"
          :key="index"
          class="grid grid-cols-[1fr_4rem_7rem_2rem] gap-2 items-center"
        >
          <input
            v-model="item.name"
            type="text"
            class="reset-base w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
            :placeholder="$t('INVOICES.CREATE.ITEM_NAME_PLACEHOLDER')"
          />
          <input
            v-model="item.quantity"
            type="number"
            min="1"
            step="1"
            class="reset-base w-full px-2 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
          />
          <input
            v-model="item.unitAmount"
            type="text"
            inputmode="decimal"
            class="reset-base w-full px-3 py-2 text-sm rounded-lg border border-n-weak bg-n-alpha-black2 text-n-slate-12"
            :placeholder="$t('INVOICES.CREATE.UNIT_AMOUNT_PLACEHOLDER')"
          />
          <NextButton
            v-if="items.length > 1"
            v-tooltip.top-end="$t('INVOICES.CREATE.REMOVE_ITEM')"
            icon="i-lucide-x"
            type="button"
            slate
            ghost
            xs
            @click="removeItem(index)"
          />
        </div>
        <div>
          <NextButton
            type="button"
            :label="$t('INVOICES.CREATE.ADD_ITEM')"
            icon="i-lucide-plus"
            slate
            link
            sm
            @click="addItem"
          />
        </div>
      </div>

      <div
        class="flex items-center justify-between px-1 pt-3 border-t border-n-weak"
      >
        <span class="text-sm font-medium text-n-slate-11">
          {{ $t('INVOICES.CREATE.TOTAL') }}
        </span>
        <span class="text-lg font-semibold text-n-slate-12 tabular-nums">
          {{ formattedTotal }}
        </span>
      </div>
    </div>
  </Dialog>
</template>
